import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  ClinicalSheetRowInterpretation,
  ClinicalSubmissionDetail,
  FacultyCaseDecisionInput,
  FacultyQueueSubmission,
} from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { EmptyState } from '../../components/EmptyState.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import {
  caseRoleLabel,
  confidenceLabel,
  isCrossOutApproveBlocked,
  reviewReasonLabel,
  rowFlagLabels,
  rowReviewStatusLabel,
  rowReviewStatusMarker,
  rowReviewStatusVariant,
  sourceCrossOutLabel,
  studentCrossOutResolutionLabel,
} from '../../lib/submissionWorkflowLabels.js';
import { rowHasMeaningfulContent } from '../../lib/rowMeaningful.js';

function FacultyRowPanel({
  row,
  onSave,
  saving,
}: {
  row: ClinicalSheetRowInterpretation;
  onSave: (input: FacultyCaseDecisionInput) => Promise<void>;
  saving: boolean;
}) {
  const [decision, setDecision] = useState<'APPROVE' | 'EXCLUDE'>(row.facultyDecision?.decision ?? 'APPROVE');
  const [facultyProcedure, setFacultyProcedure] = useState(row.facultyDecision?.facultyProcedure ?? '');
  const [facultyCaseDate, setFacultyCaseDate] = useState(row.facultyDecision?.facultyCaseDate ?? '');
  const [facultySpecialty, setFacultySpecialty] = useState(row.facultyDecision?.facultySpecialty ?? '');
  const [facultyRole, setFacultyRole] = useState(row.facultyDecision?.facultyRole ?? row.caseRole ?? 'FIRST_SCRUB');
  const flags = rowFlagLabels(row);
  const needsDisposition = rowHasMeaningfulContent(row);
  const approveBlocked = isCrossOutApproveBlocked(row);
  const instructorRow = row.reviewStatus === 'INSTRUCTOR_REVIEW_REQUIRED';
  const studentReviewRow = row.reviewStatus === 'STUDENT_REVIEW_REQUIRED';

  return (
    <div
      className={`review-row ${needsDisposition || instructorRow ? 'review-row--attention' : ''} ${
        instructorRow ? 'review-row--instructor' : ''
      }`}
      data-review-status={row.reviewStatus}
    >
      <div className="review-row__header">
        <div>
          <span className="review-row__marker" aria-label={`Review tier ${rowReviewStatusMarker(row.reviewStatus)}`}>
            {rowReviewStatusMarker(row.reviewStatus)}
          </span>
          <strong>Row {row.rowIndex}</strong>
          {studentReviewRow && (
            <span className="text-muted text-sm"> ? student review tier</span>
          )}
          {instructorRow && (
            <span className="text-sm"> ? instructor review tier</span>
          )}
        </div>
        <StatusBadge
          status={rowReviewStatusLabel(row.reviewStatus)}
          variant={rowReviewStatusVariant(row.reviewStatus)}
        />
      </div>
      {row.decisionStale && <span className="badge badge--warning">Decision stale ? save again</span>}
      {flags.length > 0 && (
        <div className="review-row__flags">
          {flags.map((flag) => (
            <span key={flag} className="badge badge--warning">
              {flag}
            </span>
          ))}
        </div>
      )}
      <div className="review-row__meta detail-list detail-list--inline text-sm">
        <span>
          <strong>Date:</strong> {row.caseDateNormalized ?? '?'}
        </span>
        <span>
          <strong>Specialty:</strong> {row.specialtyNormalized ?? '?'}
        </span>
        <span>
          <strong>Procedure:</strong> {row.procedureTextNormalized ?? '?'}
        </span>
        <span>
          <strong>Role:</strong> {caseRoleLabel(row.caseRole)}
        </span>
        <span>
          <strong>Confidence:</strong> {confidenceLabel(row.confidence ?? null)}
        </span>
      </div>
      {row.reviewReasons.length > 0 && (
        <div className="review-row__section">
          <h4 className="review-row__section-title">Review reasons</h4>
          <ul className="review-row__reasons text-sm">
            {row.reviewReasons.map((code) => (
              <li key={code}>{reviewReasonLabel(code)}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="review-row__section">
        <h4 className="review-row__section-title">Original / System Interpretation</h4>
        <p className="text-muted text-small">
          Original cross-out: {sourceCrossOutLabel(row.sourceCrossedOutState)}
        </p>
        {(row.automatedProcedureNormalized ||
          row.automatedCaseDateNormalized ||
          row.automatedSpecialtyNormalized ||
          row.automatedCaseRole) && (
          <p className="text-small text-muted">
            Automated: {row.automatedProcedureNormalized ?? '?'} ? {row.automatedCaseDateNormalized ?? '?'} ?{' '}
            {row.automatedSpecialtyNormalized ?? '?'} ? {caseRoleLabel(row.automatedCaseRole)}
          </p>
        )}
        <p className="text-small">
          Current values: {row.procedureTextNormalized ?? '?'} ? {row.caseDateNormalized ?? '?'} ?{' '}
          {row.specialtyNormalized ?? '?'} ? {caseRoleLabel(row.caseRole)}
        </p>
      </div>
      {(row.studentOccupancyResolution ||
        row.studentSignatureResolution ||
        row.studentCrossedOutResolution ||
        row.studentCrossedOutAcknowledged) && (
        <div className="review-row__section">
          <h4 className="review-row__section-title">Student Resolution</h4>
          <p className="text-muted text-small">
            {row.studentOccupancyResolution
              ? `Occupancy: ${row.studentOccupancyResolution.replace(/_/g, ' ')}`
              : ''}
            {row.studentSignatureResolution
              ? ` � Signature: ${row.studentSignatureResolution.replace(/_/g, ' ')}`
              : ''}
            {row.sourceCrossedOutState === 'UNCERTAIN'
              ? ` � Cross-out: ${studentCrossOutResolutionLabel(row.studentCrossedOutResolution)}`
              : ''}
            {row.studentCrossedOutAcknowledged ? ' � Crossed-out acknowledged' : ''}
          </p>
        </div>
      )}
      {needsDisposition ? (
        <>
          <div className="review-row__section">
            <h4 className="review-row__section-title">Faculty Final Value</h4>
          </div>
          <div className="review-row__grid">
            <label>
              Decision
              <select
                value={decision}
                onChange={(e) => setDecision(e.target.value as 'APPROVE' | 'EXCLUDE')}
              >
                <option value="APPROVE" disabled={approveBlocked}>
                  Approve Case
                </option>
                <option value="EXCLUDE">Exclude Row</option>
              </select>
            </label>
            <label>
              Approved Procedure
              <input value={facultyProcedure} onChange={(e) => setFacultyProcedure(e.target.value)} />
            </label>
            <label>
              Approved Date
              <input type="date" value={facultyCaseDate} onChange={(e) => setFacultyCaseDate(e.target.value)} />
            </label>
            <label>
              Approved Specialty
              <input value={facultySpecialty} onChange={(e) => setFacultySpecialty(e.target.value)} />
            </label>
            <label>
              Approved Role
              <select value={facultyRole} onChange={(e) => setFacultyRole(e.target.value as typeof facultyRole)}>
                <option value="FIRST_SCRUB">First Scrub</option>
                <option value="SECOND_SCRUB">Second Scrub</option>
                <option value="OBSERVATION">Observation</option>
              </select>
            </label>
          </div>
          {approveBlocked && (
            <p className="text-muted text-small">Crossed-out rows must be excluded, not approved.</p>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving}
            onClick={() =>
              void onSave({
                decision,
                facultyProcedure: decision === 'APPROVE' ? facultyProcedure : undefined,
                facultyCaseDate: decision === 'APPROVE' ? facultyCaseDate || undefined : undefined,
                facultySpecialty: decision === 'APPROVE' ? facultySpecialty : undefined,
                facultyRole: decision === 'APPROVE' ? facultyRole : undefined,
              })
            }
          >
            {saving ? 'Saving�' : 'Save Disposition'}
          </button>
        </>
      ) : (
        <p className="text-muted text-small">Blank row � no faculty disposition required.</p>
      )}
    </div>
  );
}

export function FacultySubmissionReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<ClinicalSubmissionDetail | null>(null);
  const [studentName, setStudentName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [savingRowId, setSavingRowId] = useState<string | null>(null);
  const [returnNote, setReturnNote] = useState('');
  const [approving, setApproving] = useState(false);
  const [returning, setReturning] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const [res, queueRes] = await Promise.all([
        api.getWorkflowSubmission(id),
        api.listWorkflowSubmissions(),
      ]);
      setSubmission(res.submission);
      const queued = (queueRes.data as FacultyQueueSubmission[]).find((s) => s.id === id);
      if (queued?.studentName) {
        setStudentName(queued.studentName);
      } else {
        const studentsRes = await api.listStudents({ page: 1, pageSize: 100 });
        const match = studentsRes.data.find((s) => s.id === res.submission.studentProfileId);
        setStudentName(match?.fullName ?? null);
      }
      if (res.submission.status === 'READY_FOR_INSTRUCTOR') {
        const begun = await api.beginWorkflowReview(id);
        setSubmission(begun.submission);
      }
    } catch (err) {
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        setError(
          err.status === 403
            ? 'You do not have access to review this submission.'
            : 'Submission not found.',
        );
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Failed to load submission');
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const handleSaveDecision = async (rowId: string, input: FacultyCaseDecisionInput) => {
    if (!id) return;
    setSavingRowId(rowId);
    setActionError(null);
    try {
      const res = await api.saveFacultyDecision(id, rowId, input);
      setSubmission(res.submission);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not save disposition');
    } finally {
      setSavingRowId(null);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    setApproving(true);
    setActionError(null);
    try {
      await api.approveWorkflowSubmission(id);
      navigate('/instructor/review');
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not approve submission');
    } finally {
      setApproving(false);
    }
  };

  const handleReturn = async () => {
    if (!id) return;
    setReturning(true);
    setActionError(null);
    try {
      await api.returnWorkflowSubmission(id, returnNote);
      navigate('/instructor/review');
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not return submission');
    } finally {
      setReturning(false);
    }
  };

  if (loading) return <LoadingState message="Loading submission?" />;
  if (error || !submission) {
    return (
      <div className="page">
        <ErrorBanner message={error ?? 'Submission not found'} onRetry={() => void load()} />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="Faculty Review"
        subtitle="Verify interpreted cases before approving the academic ledger"
        actions={
          <Link to="/instructor/review" className="btn btn-secondary">
            Back to Queue
          </Link>
        }
      />

      {studentName && (
        <div className="card">
          <h3 className="card__title">Student</h3>
          <p>{studentName}</p>
        </div>
      )}

      {submission.sourceEvidence && (
        <div className="card evidence-preview">
          <h3 className="card-title">Source Evidence</h3>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() =>
              window.open(api.documentContentUrl(submission.sourceEvidence!.id), '_blank', 'noopener,noreferrer')
            }
          >
            View Original Sheet
          </button>
        </div>
      )}

      {actionError && <ErrorBanner message={actionError} />}

      <div className="review-rows">
        {submission.rows.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={<span aria-hidden>??</span>}
              title="No rows to review"
              description="This submission has no interpreted case rows."
            />
          </div>
        ) : (
          submission.rows.map((row) => (
            <FacultyRowPanel
              key={row.id}
              row={row}
              saving={savingRowId === row.id}
              onSave={(input) => handleSaveDecision(row.id, input)}
            />
          ))
        )}
      </div>

      <div className="card review-actions">
        <h3 className="card-title">Submission Decision</h3>
        <p className="text-muted">
          Approve only after every relevant row has a current faculty disposition. Approved cases are written to the
          student&apos;s academic ledger.
        </p>
        {!submission.dispositionComplete && (
          <p className="banner banner-warning">Disposition incomplete � review all relevant rows before approving.</p>
        )}
        <div className="review-actions__buttons">
          <button
            type="button"
            className="btn btn-primary btn-lg"
            disabled={approving || !submission.dispositionComplete}
            onClick={() => void handleApprove()}
          >
            {approving ? 'Approving�' : 'Approve Submission'}
          </button>
        </div>
        <div className="return-panel">
          <label>
            Return note for student
            <textarea value={returnNote} onChange={(e) => setReturnNote(e.target.value)} rows={3} />
          </label>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={returning || !returnNote.trim()}
            onClick={() => void handleReturn()}
          >
            {returning ? 'Returning�' : 'Return to Student'}
          </button>
        </div>
      </div>
    </div>
  );
}
